import { useEffect, useMemo, useState } from 'react'
import type { Region, Era, Dynasty, Figure, HistoricalEvent } from './types'
import regionsData    from './data/regions.json'
import erasData       from './data/eras.json'
import eventsData     from './data/events.json'
import dynastiesData  from './data/dynasties.json'
import figuresData    from './data/figures.json'

import Header from './components/Header'
import EraRibbon from './components/EraRibbon'
import MapView from './components/MapView'
import DetailPanel from './components/DetailPanel'
import ErrorBoundary from './components/ErrorBoundary'
import ThemePicker from './components/ThemePicker'
import HelpPage from './components/HelpPage'

import { useUrlState } from './utils/url'
import { filterEvents } from './utils/filter'
import { useTheme } from './hooks/useTheme'

const regions    = regionsData    as Region[]
const eras       = erasData       as Era[]
const events     = eventsData     as HistoricalEvent[]
const dynasties  = dynastiesData  as Dynasty[]
const figures    = figuresData    as Figure[]

export default function App() {
  const [urlState, setUrlState] = useUrlState()
  const [hoverRegionId, setHoverRegionId] = useState<string | null>(null)
  const [searchResetKey, setSearchResetKey] = useState(0)
  const { theme, setTheme } = useTheme()

  const regionsById   = useMemo(() => new Map(regions.map(r => [r.id, r])),       [])
  const dynastiesById = useMemo(() => new Map(dynasties.map(d => [d.id, d])),     [])
  const figuresById   = useMemo(() => new Map(figures.map(f => [f.id, f])),       [])
  const erasById      = useMemo(() => new Map(eras.map(e => [e.id, e])),          [])

  const selectedRegion = urlState.region ? regionsById.get(urlState.region) ?? null : null
  const selectedEra    = urlState.era    ? erasById.get(urlState.era)       ?? null : null
  const selectedYear   = urlState.year
  const selectedFigure = urlState.figure ? figuresById.get(urlState.figure) ?? null : null

  // Listen for the empty-state quick-jump events
  useEffect(() => {
    function onYear(e: Event) {
      const y = (e as CustomEvent).detail as number
      setUrlState({ year: y, era: null })
    }
    window.addEventListener('atlas:set-year', onYear)
    return () => window.removeEventListener('atlas:set-year', onYear)
  }, [setUrlState])

  const filtered = useMemo<HistoricalEvent[]>(() => {
    return filterEvents(events, {
      regionId:      selectedRegion?.id ?? null,
      figureId:      selectedFigure?.id ?? null,
      year:          selectedYear,
      eraStartYear:  selectedYear === null ? selectedEra?.startYear ?? null : null,
      eraEndYear:    selectedYear === null ? selectedEra?.endYear   ?? null : null,
      yearTolerance: 25
    })
  }, [selectedRegion, selectedFigure, selectedYear, selectedEra])

  // Map needs the (time + figure)-filtered events — irrespective of region —
  // to know which regions to highlight. When a figure is selected, the map
  // lights up only the regions that figure touches in this period.
  const timeFilteredAll = useMemo<HistoricalEvent[]>(() => {
    return filterEvents(events, {
      figureId:      selectedFigure?.id ?? null,
      year:          selectedYear,
      eraStartYear:  selectedYear === null ? selectedEra?.startYear ?? null : null,
      eraEndYear:    selectedYear === null ? selectedEra?.endYear   ?? null : null,
      yearTolerance: 25
    })
  }, [selectedFigure, selectedYear, selectedEra])

  return (
    <div className="relative min-h-screen flex flex-col">
      <Header
        onSelectRegion={id => setUrlState({ region: id, figure: null, view: null })}
        // Picking a figure starts a fresh exploration thread — clear region,
        // era, year, and the help view so the focus is purely on that person.
        onSelectFigure={id => setUrlState({ figure: id, region: null, era: null, year: null, view: null })}
        onResetHome={() => {
          setUrlState({ region: null, era: null, year: null, figure: null, view: null })
          // Remount the SearchBar so its uncontrolled text input clears too.
          setSearchResetKey(k => k + 1)
        }}
        hasAnySelection={selectedRegion !== null || selectedEra !== null || selectedYear !== null || selectedFigure !== null}
        searchResetKey={searchResetKey}
      />

      {urlState.view === 'help' ? (
        <HelpPage onClose={() => setUrlState({ view: null })} />
      ) : (
        <>
          <EraRibbon
            eras={eras}
            selectedEraId={selectedEra?.id ?? null}
            selectedYear={selectedYear}
            onSelectEra={id => setUrlState({ era: id, year: null })}
            onSelectYear={y => setUrlState({ year: y, era: null })}
            onOpenHelp={() => setUrlState({ view: 'help' })}
          />

          <main className="flex-1 min-h-0 max-w-[1800px] w-full mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[1.7fr_1fr] lg:grid-rows-[minmax(0,1fr)] gap-6 relative z-[1]">
            {/* On mobile (single-column), the detail panel sits ABOVE the map
                so text content is closer to the fold and the map sits below
                as a visual orientation aid. Desktop layout (map on the left,
                panel on the right) is unchanged via lg:order-*. */}
            <section className="order-2 lg:order-1 min-h-[360px] lg:min-h-0 relative">
              <ErrorBoundary label="map view">
                <MapView
                  regions={regions}
                  events={timeFilteredAll}
                  hasTimeFilter={selectedYear !== null || selectedEra !== null}
                  selectedRegionId={selectedRegion?.id ?? null}
                  hoverRegionId={hoverRegionId}
                  onSelectRegion={id => setUrlState({ region: id })}
                  onHoverRegion={setHoverRegionId}
                />
              </ErrorBoundary>
            </section>

            <section className="order-1 lg:order-2 min-h-[360px] lg:min-h-0 relative">
              <ErrorBoundary label="detail panel">
                <DetailPanel
                  region={selectedRegion}
                  era={selectedEra}
                  year={selectedYear}
                  figure={selectedFigure}
                  events={filtered}
                  regionsById={regionsById}
                  dynastiesById={dynastiesById}
                  figuresById={figuresById}
                  onSelectRegion={id => setUrlState({ region: id, figure: null })}
                  onSelectFigure={id => setUrlState({ figure: id, region: null, era: null, year: null })}
                  onClearRegion={() => setUrlState({ region: null })}
                  onClearTime={() => setUrlState({ era: null, year: null })}
                  onClearFigure={() => setUrlState({ figure: null })}
                />
              </ErrorBoundary>
            </section>
          </main>
        </>
      )}

      <footer className="border-t border-ink-300/10 bg-parchment-50/60 mt-2">
        <div className="max-w-[1800px] mx-auto px-6 py-3 flex items-center justify-between text-[11px] text-ink-50 gap-4 flex-wrap">
          <span>Bhāvan Atlas · v0.1 · Phase 1 skeleton</span>
          <ThemePicker theme={theme} onSetTheme={setTheme} />
          <span className="italic">in the voice of <em className="italic">History &amp; Culture of the Indian People</em>, R. C. Majumdar (gen. ed.), Bharatiya Vidya Bhavan</span>
        </div>
      </footer>
    </div>
  )
}
